import isNumber from 'lodash/isNumber';
import Player from '../models/Player';
import htmlMediaEvents from '../constants/htmlMediaEvents';
import hlsEvents from './constants/hlsEvents';
import getElapsedPercent from '../utils/getElapsedPercent';
import playerName from '../constants/playerName';
import mediaFormats from '../constants/mediaFormats';

const hlsErrorTypes = {
  networkError: 'networkError',
};
const minimumPreloadBufferSeconds = [3, 2, 1];
const DEFAULT_PRELOAD_BUFFER_SECONDS = 2;

function onError(callback, format) {
  return (event, data = {}) => {
    const { details, fatal, response, type, url } = data;
    const error = new Error(`${playerName.HlsJsPlayer} error`);
    const isNetworkError = type === hlsErrorTypes.networkError;
    // format errors are also considered network errors by players, so we'll use this flag to
    // allow logging of potential formatting compatibility issues
    const isFormatError = format !== mediaFormats.hls;
    error.errorData = {
      event,
      details,
      fatal,
      response,
      type,
      url,
      isNetworkError,
      isFormatError,
    };
    return callback(error);
  };
}

export default class HlsJsPlayer extends Player {
  #bandwidthEstimate = Number.POSITIVE_INFINITY;

  constructor(hlsjs, audioElement, containerId) {
    super(playerName.HlsJsPlayer);
    this.hlsjs = hlsjs;
    this.audioElement = audioElement;
    this.containerId = containerId;
    this.isLiveSeek = false;
  }

  #onLevelLoaded = (event, data) => {
    // Round to nearest digit representing an index of 10mbps increments
    // (e.g., 1mbps -> 0, 5mbps -> 1, 10mbps -> 1, 15mbps -> 2, 20mbps -> 2, etc.)
    this.#bandwidthEstimate = Math.round(data.stats.bwEstimate / 10_000_000);
  };

  async #waitForPreloadBuffer() {
    await new Promise((resolve) => {
      const onBufferAppended = (event, data) => {
        if (
          data.timeRanges?.audio?.length &&
          data.timeRanges?.audio?.end(0) >=
            (minimumPreloadBufferSeconds[this.#bandwidthEstimate] ||
              DEFAULT_PRELOAD_BUFFER_SECONDS)
        ) {
          this.loadStopped = true;
          this.hlsjs.removeEventListener(
            hlsEvents.BUFFER_APPENDED,
            onBufferAppended,
          );
          this.hlsjs.stopLoad();
          resolve();
        }
      };

      this.hlsjs.addEventListener(hlsEvents.BUFFER_APPENDED, onBufferAppended);
    });
  }

  init() {
    return this.hlsjs.init();
  }

  inheritConfigs(configs) {
    Object.assign(this, configs);
  }

  on(event, callback) {
    if (hlsEvents[event]) {
      this.hlsjs.addEventListener(event, callback);
    } else {
      let callbackToUse = callback;

      if (event === htmlMediaEvents.error) {
        callbackToUse = onError(callback, this.format);
        this.cachedErrorCallback = callbackToUse;
      }

      this.audioElement.addEventListener(event, callbackToUse);
    }
  }

  off(event, callback) {
    if (hlsEvents[event]) {
      this.hlsjs.removeEventListener(event, callback);
    } else {
      let callbackToUse = callback;

      if (event === htmlMediaEvents.error) {
        callbackToUse = this.cachedErrorCallback;
        delete this.cachedErrorCallback;
      }

      this.audioElement.removeEventListener(event, callbackToUse);
    }
  }

  load(url, format, position, trackingUrl, isPreload) {
    this.loadStopped = false;
    this.format = format;
    return new Promise(async (resolve, reject) => {
      this.hlsjs.addEventListener(
        hlsEvents.ERROR,
        onError(reject, format),
        true,
      );
      this.hlsjs.addEventListener(hlsEvents.LEVEL_LOADED, this.#onLevelLoaded, {
        once: true,
      });

      try {
        await this.hlsjs.attachMedia(this.audioElement);
        await this.hlsjs.loadSource(url, position, this.isLiveSeek);

        if (isPreload) {
          await this.#waitForPreloadBuffer();
        }

        resolve();
      } catch (e) {
        // just in case the event isn't hlsEvents.ERROR, for whatever reason
        onError(reject, format)(e);
      }
    });
  }

  play() {
    return new Promise((resolve, reject) => {
      const errorCallback = onError(reject, this.format);
      this.audioElement.addEventListener(htmlMediaEvents.error, errorCallback, {
        once: true,
      });
      this.audioElement.addEventListener(htmlMediaEvents.playing, resolve, {
        once: true,
      });
      this.hlsjs.addEventListener(hlsEvents.LEVEL_LOADED, this.#onLevelLoaded);

      if (this.loadStopped) {
        this.loadStopped = false;
        this.hlsjs.startLoad();
      }

      this.audioElement.play();
    });
  }

  pause() {
    this.audioElement.pause();
  }

  stop() {
    this.audioElement.pause();
    this.hlsjs.off();
  }

  async unload() {
    this.hlsjs.off();
    this.hlsjs.detachMedia();
    await this.hlsjs.destroy();
    await this.hlsjs.init();
  }

  async destroy() {
    this.hlsjs.off();
    await this.hlsjs.destroy();
    const container = document.getElementById(this.containerId);
    if (!container) {
      return;
    }
    container.parentElement.removeChild(container);
  }

  canChangePlaybackRate() {
    const { playbackRate } = this.audioElement;
    return !!playbackRate && playbackRate > 0;
  }

  // https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/playbackRate
  getPlaybackRate() {
    return this.audioElement.playbackRate;
  }

  // https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/playbackRate
  changePlaybackRate(rate) {
    this.audioElement.playbackRate = rate;
  }

  setVolume(volume) {
    if (isNumber(volume)) {
      this.audioElement.volume = volume / 100;
    }
  }

  getVolume() {
    return this.audioElement.volume;
  }

  toggleMute() {
    this.audioElement.muted = !this.audioElement.muted;
  }

  setTitle(title) {
    this.audioElement.setAttribute('title', title);
  }

  getTimes() {
    const audio = this.audioElement;
    const duration = Math.floor(audio.duration || 0);
    const elapsedSeconds = Math.floor(audio.currentTime || 0);
    const elapsedPercent = getElapsedPercent(elapsedSeconds, duration);
    return {
      duration,
      elapsedSeconds,
      elapsedPercent,
    };
  }
}
